<template>
    <div class="row">
        <FormGroup :label="$t('labels.api_token')" class="col-lg-4">
            <Input v-model="token" @blur="fetchCounterparties()"/>
        </FormGroup>
        <FormGroup :label="$t('labels.file_fetching_enabled')" class="col-lg-4">
            <AppSwitch v-model="fileFetchingEnabled"/>
        </FormGroup>
        <FormGroup :label="$t('labels.company_number_creating_enabled')" class="col-lg-4">
            <AppSwitch v-model="companyNumberCreatingEnabled"/>
        </FormGroup>
    </div>
    <template v-if="companyNumberCreatingEnabled">
        <AppFieldset :title="$t('labels.shipment_details')">
            <div class="row">
                <FormGroup :label="$t('labels.counterparty')" class="col-lg-3" required>
                    <AppSelect
                        @update:model-value="selectCounterparty($event)"
                        :model-value="counterparty?.id"
                        :selected-value="counterparty?.name"
                        :options="counterparties"
                        :disabled="!token"
                        option-key="id"
                        option-value="name"
                    />
                </FormGroup>
                <FormGroup :label="$t('labels.contact')" class="col-lg-3" required>
                    <AppSelect
                        @update:model-value="contact = $event"
                        :model-value="contact?.id"
                        :selected-value="contact?.name"
                        :options="contacts"
                        :disabled="!counterparty"
                        option-key="id"
                        option-value="name"
                    />
                </FormGroup>
                <FormGroup :label="$t('labels.city')" class="col-lg-3" required>
                    <AppAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${ApiCredentialsServiceEnum.NOVAPOST}/cities`"
                        :model-value="city?.uuid"
                        :selected-value="formatDeliveryCityName(city)"
                        @update:model-value="(newCity) => {city = newCity; warehouse = null}"
                        :query-params="{
                            orders: [
                                { column: 'name', dir: 'asc'}
                            ]
                        }"
                    >
                        <template #optionItem="{option}">
                            {{ formatDeliveryCityName(option) }}
                        </template>
                    </AppAutoComplete>
                </FormGroup>
                <FormGroup :label="$t('labels.novapost_department')" class="col-lg-3" required>
                    <AppAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${ApiCredentialsServiceEnum.NOVAPOST}/cities/${city?.uuid}/warehouses`"
                        :model-value="warehouse?.uuid"
                        :selected-value="warehouse?.name"
                        :disabled="!city"
                        @update:model-value="warehouse = $event"
                        :query-params="{
                            orders: [
                                { column: 'name', dir: 'asc'}
                            ]
                        }"
                    />
                </FormGroup>
                <FormGroup :label="$t('labels.payer_type')" class="col-lg-4">
                    <AppSelect
                        @update:model-value="payerType = $event"
                        :model-value="payerType?.key"
                        :selected-value="payerType?.name"
                        :options="payerTypes"
                        option-key="key"
                        option-value="name"
                        can-be-empty
                    />
                </FormGroup>
                <FormGroup :label="$t('labels.backward_delivery_payer_type')" class="col-lg-4" :required="isBackwardCodMode">
                    <AppSelect
                        @update:model-value="backwardDeliveryPayerType = $event"
                        :model-value="backwardDeliveryPayerType?.key"
                        :selected-value="backwardDeliveryPayerType?.name"
                        :options="payerTypes"
                        :disabled="!isBackwardCodMode"
                        option-key="key"
                        option-value="name"
                        can-be-empty
                    />
                </FormGroup>
                <FormGroup :label="$t('labels.payment_method')" class="col-lg-4">
                    <AppSelect
                        @update:model-value="paymentMethod = $event"
                        :model-value="paymentMethod?.key"
                        :selected-value="paymentMethod?.name"
                        :options="paymentMethods"
                        option-key="key"
                        option-value="name"
                        can-be-empty
                    />
                </FormGroup>
            </div>
        </AppFieldset>
        <AppFieldset :title="$t('labels.cargo_details')">
            <div class="row">
                <FormGroup :label="$t('labels.cargo_type')" class="col-lg-2 col-md-4">
                    <AppSelect
                        @update:model-value="cargoType = $event"
                        :model-value="cargoType?.key"
                        :selected-value="cargoType?.name"
                        :options="cargoTypes"
                        option-key="key"
                        option-value="name"
                        can-be-empty
                    />
                </FormGroup>
                <FormGroup :label="$t('labels.seats_amount')" class="col-lg-2 col-md-4">
                    <Input v-model="dimensions.seats_amount" type="number"/>
                </FormGroup>
                <FormGroup :label="$t('labels.weight')" class="col-lg-2 col-md-4">
                    <Input v-model="dimensions.weight" type="number"/>
                </FormGroup>
                <FormGroup :label="$t('labels.length')" class="col-lg-2 col-md-4">
                    <Input v-model="dimensions.length" type="number"/>
                </FormGroup>
                <FormGroup :label="$t('labels.width')" class="col-lg-2 col-md-4">
                    <Input v-model="dimensions.width" type="number"/>
                </FormGroup>
                <FormGroup :label="$t('labels.height')" class="col-lg-2 col-md-4">
                    <Input v-model="dimensions.height" type="number"/>
                </FormGroup>
            </div>
        </AppFieldset>
    </template>
</template>
<script>
import FormGroup from '../../../../../components/new/form/group/FormGroup.vue';
import Input from '../../../../../components/new/form/Input.vue';
import AppSelect from '../../../../../components/new/form/select/AppSelect.vue';
import AppSwitch from '../../../../../components/new/form/switch/AppSwitch.vue';
import AppAutoComplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import AppFieldset from '../../../../../components/new/AppFieldset.vue';

import { ApiCredentialsServiceEnum } from '../../../../enums/ApiCredentialsServiceEnum';
import { NovapostPayerTypeEnum } from '../../../../enums/NovapostPayerTypeEnum';
import { NovapostPaymentMethodEnum } from '../../../../enums/NovapostPaymentMethodEnum';
import { NovapostCargoTypeEnum } from '../../../../enums/NovapostCargoTypeEnum';
import { NovapostCodModeEnum } from '../../../../enums/NovapostCodModeEnum';
import { NovapostCounterpartyTypeEnum } from '../../../../enums/NovapostCounterpartyTypeEnum';
import { formatDeliveryCityName } from '../../../../../utils';
import { httpClient } from '../../../../../services/HttpClient';
import { makeSenderCity, makeSenderWarehouse, makeDimensionsForm, buildDimensions, buildSenderAddressFields } from './senderDefaultFields';

export default {
    name: "NovapostCredentialFields",
    components: {
        FormGroup, Input, AppSelect, AppSwitch, AppAutoComplete, AppFieldset
    },
    props: {
        credential: {
            type: Object,
            default: null
        }
    },
    emits: ['update:loading'],
    data() {
        let settings = this.credential?.settings ?? {};
        let defaultFields = settings.default_fields ?? {};

        let payerTypes = [
            {
                key: NovapostPayerTypeEnum.SENDER,
                name: this.$t(`enums.NovapostPayerTypeEnum.${NovapostPayerTypeEnum.SENDER}`)
            },
            {
                key: NovapostPayerTypeEnum.RECIPIENT,
                name: this.$t(`enums.NovapostPayerTypeEnum.${NovapostPayerTypeEnum.RECIPIENT}`)
            },
            {
                key: NovapostPayerTypeEnum.THIRD_PERSON,
                name: this.$t(`enums.NovapostPayerTypeEnum.${NovapostPayerTypeEnum.THIRD_PERSON}`)
            }
        ];

        let paymentMethods = [
            {
                key: NovapostPaymentMethodEnum.CASH,
                name: this.$t(`enums.NovapostPaymentMethodEnum.${NovapostPaymentMethodEnum.CASH}`)
            },
            {
                key: NovapostPaymentMethodEnum.NON_CASH,
                name: this.$t(`enums.NovapostPaymentMethodEnum.${NovapostPaymentMethodEnum.NON_CASH}`)
            }
        ];

        let cargoTypes = [
            {
                key: NovapostCargoTypeEnum.CARGO,
                name: this.$t(`enums.NovapostCargoTypeEnum.${NovapostCargoTypeEnum.CARGO}`)
            },
            {
                key: NovapostCargoTypeEnum.DOCUMENTS,
                name: this.$t(`enums.NovapostCargoTypeEnum.${NovapostCargoTypeEnum.DOCUMENTS}`)
            },
            {
                key: NovapostCargoTypeEnum.TIRES_WHEELS,
                name: this.$t(`enums.NovapostCargoTypeEnum.${NovapostCargoTypeEnum.TIRES_WHEELS}`)
            },
            {
                key: NovapostCargoTypeEnum.PALLET,
                name: this.$t(`enums.NovapostCargoTypeEnum.${NovapostCargoTypeEnum.PALLET}`)
            },
            {
                key: NovapostCargoTypeEnum.PARCEL,
                name: this.$t(`enums.NovapostCargoTypeEnum.${NovapostCargoTypeEnum.PARCEL}`)
            }
        ];

        return {
            ApiCredentialsServiceEnum,
            token: this.credential?.token ?? '',
            fileFetchingEnabled: settings.file_fetching_enabled ?? false,
            companyNumberCreatingEnabled: settings.company_number_creating_enabled ?? false,
            counterparty: null,
            counterparties: [],
            loadedCounterpartiesToken: null,
            contact: null,
            contacts: [],
            city: makeSenderCity(defaultFields),
            warehouse: makeSenderWarehouse(defaultFields),
            payerTypes,
            payerType: payerTypes.find(payerType => payerType.key === defaultFields.payer_type) ?? null,
            backwardDeliveryPayerType: payerTypes.find(payerType => payerType.key === defaultFields.backward_delivery_payer_type) ?? null,
            paymentMethods,
            paymentMethod: paymentMethods.find(paymentMethod => paymentMethod.key === defaultFields.payment_method) ?? null,
            cargoTypes,
            cargoType: cargoTypes.find(cargoType => cargoType.key === defaultFields.cargo_type) ?? null,
            dimensions: makeDimensionsForm(defaultFields)
        };
    },
    computed: {
        defaultFields() {
            let defaultFields = this.credential?.settings?.default_fields ?? {};

            return defaultFields;
        },
        codMode() {
            if (!this.counterparty) {
                return null;
            }

            let codMode = this.counterparty.type === NovapostCounterpartyTypeEnum.ORGANIZATION
                ? NovapostCodModeEnum.AFTERPAYMENT
                : NovapostCodModeEnum.BACKWARD;

            return codMode;
        },
        isBackwardCodMode() {
            let isBackward = this.codMode === NovapostCodModeEnum.BACKWARD;

            return isBackward;
        }
    },
    watch: {
        isBackwardCodMode(isBackward) {
            if (!isBackward) {
                this.backwardDeliveryPayerType = null;
            }
        }
    },
    methods: {
        formatDeliveryCityName,
        fetchCounterparties() {
            if (!this.token) {
                this.counterparties = [];
                this.loadedCounterpartiesToken = null;

                return this.selectCounterparty(null);
            }

            if (this.token === this.loadedCounterpartiesToken) {
                return;
            }

            this.loadedCounterpartiesToken = this.token;
            this.$emit('update:loading', true);

            return httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/delivery/providers/${ApiCredentialsServiceEnum.NOVAPOST}/${encodeURIComponent(this.token)}/counterparties`).then(response => {
                this.counterparties = response.data.data;
            }).catch(() => {
                this.counterparties = [];
                this.loadedCounterpartiesToken = null;
            }).then(() => {
                let counterparty = this.counterparties.find(item => item.id === this.defaultFields.sender_id) ?? null;

                return this.selectCounterparty(counterparty);
            }).finally(() => {
                this.$emit('update:loading', false);
            });
        },
        selectCounterparty(counterparty) {
            this.counterparty = counterparty;

            return this.fetchContacts();
        },
        fetchContacts() {
            this.contact = null;
            this.contacts = [];

            if (!this.counterparty) {
                return;
            }

            return httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/delivery/providers/${ApiCredentialsServiceEnum.NOVAPOST}/${encodeURIComponent(this.token)}/counterparties/${this.counterparty.id}/contact-persons`).then(response => {
                this.contacts = response.data.data;
                this.contact = this.contacts.find(item => item.id === this.defaultFields.sender_contact_id) ?? null;
            }).catch(() => {
                this.contacts = [];
            });
        },
        buildRequest() {
            let request = {
                token: this.token,
                settings: {
                    file_fetching_enabled: this.fileFetchingEnabled,
                    company_number_creating_enabled: this.companyNumberCreatingEnabled,
                    default_fields: {
                        ...this.defaultFields,
                        ...buildSenderAddressFields(this.city, this.warehouse),
                        dimensions: buildDimensions(this.dimensions),
                        sender_id: this.counterparty?.id ?? null,
                        sender_contact_id: this.contact?.id ?? null,
                        sender_phone: this.contact?.phone ?? null,
                        payer_type: this.payerType?.key ?? null,
                        payment_method: this.paymentMethod?.key ?? null,
                        cargo_type: this.cargoType?.key ?? null,
                        cod_mode: this.codMode,
                        backward_delivery_payer_type: this.isBackwardCodMode ? this.backwardDeliveryPayerType?.key ?? null : null
                    }
                }
            };

            return request;
        }
    },
    created() {
        this.fetchCounterparties();
    }
}
</script>
